import { useSearchParams } from "react-router";
import { AdminDataNotice } from "@/components/admin/data-notice";
import { RefreshButton } from "@/components/admin/refresh-button";
import { TraceFilters } from "@/components/admin/trace-filters";
import { useAdminView } from "@shared/client/admin-view";
import { Link } from "@shared/client/navigation";
import { usePageTitle } from "@shared/client/view";
import { AdminChip, AdminEmpty, AdminPanel, StatCard, StatGroup } from "@shared/components/blocks";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@shared/components/ui/table";
import type { AgentTraceSummary } from "@shared/lib/admin-db";
import { formatDateTime, formatNumber } from "@shared/lib/format";
import { AGENT_TRACE_KIND_LABELS, AGENT_TRACE_PROVIDER_LABELS } from "./agent-labels";

export default function AdminAgentsPage() {
  usePageTitle("Agents IA — Administration Hune");
  const [params] = useSearchParams();
  const provider = params.get("provider") ?? "all";
  const kind = params.get("kind") ?? "all";
  const status = params.get("status") ?? "all";
  const run = params.get("run") ?? "";
  const { data, error: loadError } = useAdminView<{ traces: AgentTraceSummary[] }>("/api/views/admin/agents");
  if (!data && !loadError) return null;
  const traces = data?.traces ?? [];

  const errorCount = traces.filter((trace) => trace.status === "error").length;
  const totalTokens = traces.reduce((sum, trace) => sum + (trace.total_tokens ?? 0), 0);
  const durations = traces.filter((trace) => trace.duration_ms !== null);
  const avgDuration = durations.length
    ? Math.round(durations.reduce((sum, trace) => sum + (trace.duration_ms ?? 0), 0) / durations.length)
    : null;

  return (
    <>
      <StatGroup className="mb-4.5" columns={4}>
        <StatCard label="Appels affichés" value={formatNumber(traces.length)} />
        <StatCard label="Erreurs" tint={errorCount > 0 ? "rose" : "blue"} value={formatNumber(errorCount)} />
        <StatCard label="Tokens consommés" tint="teal" value={formatNumber(totalTokens)} />
        <StatCard label="Durée moyenne" value={avgDuration === null ? "—" : `${formatNumber(avgDuration)} ms`} />
      </StatGroup>

      <AdminPanel flush>
        <div className="flex flex-wrap items-center gap-3 border-b px-4 py-3">
          <TraceFilters kind={kind} provider={provider} run={run} status={status} />
          <RefreshButton />
        </div>

        {loadError ? (
          <div className="p-4"><AdminDataNotice error={loadError} /></div>
        ) : traces.length === 0 ? (
          <AdminEmpty>
            Aucune trace pour le moment. Elles apparaissent dès la prochaine analyse
            (pensez à appliquer la migration 011_agent_observability.sql).
          </AdminEmpty>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="pl-5">Quand</TableHead>
                <TableHead>Appel</TableHead>
                <TableHead className="max-sm:hidden">Cible</TableHead>
                <TableHead className="max-sm:hidden">Modèle</TableHead>
                <TableHead className="text-right max-sm:hidden">Tokens</TableHead>
                <TableHead className="max-sm:hidden">Durée</TableHead>
                <TableHead className="pr-5">Statut</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {traces.map((trace) => (
                <TableRow className={trace.status === "error" ? "bg-destructive/4" : undefined} key={trace.id}>
                  <TableCell className="pl-5 whitespace-nowrap tabular-nums">
                    <Link className="font-semibold hover:text-foreground" href={`/admin/agents/${trace.id}`}>
                      {formatDateTime(trace.created_at)}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <AdminChip tone="role">{AGENT_TRACE_PROVIDER_LABELS[trace.provider]}</AdminChip>
                    <small className="mt-1 block text-[12px] text-muted-foreground">
                      {AGENT_TRACE_KIND_LABELS[trace.kind]}
                    </small>
                  </TableCell>
                  <TableCell className="max-w-[240px] max-sm:hidden">
                    <span className="block truncate">{trace.organization?.name || trace.run?.product_url || "—"}</span>
                    {trace.organization && trace.run?.product_url && (
                      <small className="block truncate text-[12px] text-muted-foreground">{trace.run.product_url}</small>
                    )}
                  </TableCell>
                  <TableCell className="max-sm:hidden"><code className="font-mono text-[12px]">{trace.model ?? "—"}</code></TableCell>
                  <TableCell className="text-right whitespace-nowrap tabular-nums max-sm:hidden">
                    {trace.total_tokens === null ? "—" : formatNumber(trace.total_tokens)}
                    {trace.input_tokens !== null && trace.output_tokens !== null && (
                      <small className="block text-[12px] text-muted-foreground">
                        {formatNumber(trace.input_tokens)} in · {formatNumber(trace.output_tokens)} out
                      </small>
                    )}
                  </TableCell>
                  <TableCell className="whitespace-nowrap tabular-nums max-sm:hidden">
                    {trace.duration_ms === null ? "—" : `${formatNumber(trace.duration_ms)} ms`}
                  </TableCell>
                  <TableCell className="pr-5">
                    <span className="flex items-center gap-2">
                      <AdminChip tone={trace.status === "error" ? "churned" : "active"}>
                        {trace.status === "error" ? "erreur" : "succès"}
                      </AdminChip>
                      <Link className="text-[12px] font-semibold text-muted-foreground hover:text-foreground" href={`/admin/agents/${trace.id}`}>
                        Détail →
                      </Link>
                    </span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </AdminPanel>
    </>
  );
}
